import dayjs, { Dayjs } from 'dayjs';
import { isNil } from 'lodash';
import {
  dateFormatRange,
  fromHoursStringToDayjs,
  isToday,
} from '../../helpers/dateTimes';
import { DateTimePickerModel, TimePickerPreset } from '../../models';
import { TimeSpan } from './components/HourSlider/HourSlider';

export const startingTimeSlot = 7;
export const endingTimeSlot = 23;

export const getValidPresets = (
  timePickerModel: DateTimePickerModel,
  selectedDate: Dayjs,
  presets: TimePickerPreset[]
) => {
  const personalizedPreset = presets.find((p) => !p.timeRangeEnd);

  const validPresets = presets.filter(
    (p) => !isNil(getValidPresetSlot(timePickerModel, selectedDate, p))
  );
  validPresets.unshift(personalizedPreset!);
  return validPresets;
};

export const getValidPresetSlot = (
  timePickerModel: DateTimePickerModel,
  selectedDate: Dayjs,
  { timeRangeEnd, timeRangeStart, isCurrentDay }: TimePickerPreset
): { from: Dayjs; to: Dayjs } | null => {
  if (!timeRangeEnd || !timeRangeStart) return null;

  if (isCurrentDay) {
    const today = timePickerModel.availableDates?.find((d) => isToday(d));
    if (!today) return null;
  }

  const presetStartTime = fromHoursStringToDayjs(timeRangeStart!);
  const presetEndTime = fromHoursStringToDayjs(timeRangeEnd!);
  let dateToSearch: Dayjs = isCurrentDay ? dayjs() : selectedDate;

  return searchValidRange(
    presetStartTime,
    presetEndTime,
    dateToSearch,
    timePickerModel
  );
};

export const searchValidRange = (
  from: Dayjs,
  to: Dayjs,
  date: Dayjs,
  { startTimeRanges }: DateTimePickerModel
) => {
  const dayStartTimes = startTimeRanges[date.format(dateFormatRange)];
  if (dayStartTimes) {
    const validStartTimes = Object.keys(dayStartTimes)
      .filter((st) => {
        return isTimeInRange(st, from, to);
      })
      .sort();
    const validRanges = validStartTimes
      .map((st) => {
        const validEndTime = Object.keys(dayStartTimes[st])
          .sort()
          .reverse()
          .find((et) => isTimeInRange(et, from, to));
        if (validEndTime === undefined) return undefined;

        return {
          from: fromHoursStringToDayjs(st),
          to: fromHoursStringToDayjs(validEndTime),
        };
      })
      .filter((e) => e !== undefined);

    if (validRanges.length > 0) return validRanges[0]!;
  }

  return null;
};

export const isTimeInRange = (
  time: string | Dayjs,
  from: dayjs.Dayjs,
  to: dayjs.Dayjs
) => {
  const dayjsSt =
    typeof time === 'string' ? fromHoursStringToDayjs(time) : time;
  const isInRange = dayjsSt.isSameOrAfter(from) && dayjsSt.isSameOrBefore(to);
  return isInRange;
};

export function calculateTimeSpansByRanges(startTimes: string[]) {
  const availableTimeSlotMap: Record<string, boolean> = startTimes.reduce(
    (pv: Record<string, any>, cv) => {
      pv[cv] = true;
      return pv;
    },
    {}
  );

  let dateTime = dayjs().set('hours', startingTimeSlot).set('minute', 0);
  const endDateTime = dayjs().set('hours', endingTimeSlot).set('minute', 0);

  const calculatedSpans: TimeSpan[] = [];

  while (endDateTime.diff(dateTime, 'minute') >= 0) {
    const formattedSpan = dateTime.format('HH:mm');
    let timeSpan: TimeSpan;

    if (availableTimeSlotMap[formattedSpan]) {
      timeSpan = {
        isEnable: true,
        time: formattedSpan,
      };
    } else {
      timeSpan = {
        isEnable: false,
        time: formattedSpan,
      };
    }

    calculatedSpans.push(timeSpan);
    dateTime = dateTime.add(30, 'minutes'); // Add range steps
  }
  return calculatedSpans;
}
